import { useMemo } from "react";

import {
  ExperienceDto,
  ExperienceListDto,
  ExperienceListWithOffersDto,
  GetExperiencesResponseBodyType,
  PersonalizationOfferType,
  PersonalizationTestType,
  ScheduleDto,
} from "@intelligems/ig-types";
import { useDialogs } from "@src/hooks/dialog";
import { FoundExperimentDetails } from "@src/types/experience";

export const useOnPersonalizationActionClick = () => {
  const { openDialog } = useDialogs();

  return ({
    personalization,
    schedules,
    isUsedInTest,
    onActionClick,
  }: {
    personalization: ExperienceListDto;
    schedules: ScheduleDto[];
    isUsedInTest: FoundExperimentDetails;
    onActionClick: (personalization: ExperienceListDto) => void;
  }) => {
    const foundSchedules = schedules.filter(
      (schedule) => schedule.experienceId === personalization.id
    );
    if (foundSchedules.length > 0) {
      openDialog("activeSchedule", {
        schedules: foundSchedules,
        experience: personalization,
      });
    } else {
      if (isUsedInTest.id && personalization.status !== "started") {
        openDialog("relatedExperienceActive", {
          personalization,
          isUsedInTestName: isUsedInTest.name,
        });
      } else {
        onActionClick(personalization);
      }
    }
  };
};

const searchInExperienceData = (
  experience: ExperienceDto,
  searchTerm: string
): boolean => {
  if (!experience || !searchTerm) return false;

  const lowerSearchTerm = searchTerm.toLowerCase();
  const variationIdMap = experience.variations.map((variation) => variation.id);
  return (
    experience.name?.toLowerCase().includes(lowerSearchTerm) ||
    experience.id?.toLowerCase().includes(lowerSearchTerm) ||
    variationIdMap.includes(lowerSearchTerm) ||
    false
  );
};

export const useSearchExperienceData = (
  experiences: ExperienceDto[],
  searchInput: string,
  typeFilters: (PersonalizationOfferType | PersonalizationTestType)[],
  statusFilters: string[],
  renderLocation: string
) => {
  return useMemo(() => {
    let filtered = [...experiences];

    if (searchInput) {
      filtered = experiences.filter((experience) =>
        searchInExperienceData(experience, searchInput)
      );
    }

    if (statusFilters.length > 0) {
      filtered = filtered.filter((experience) => {
        const isArchived = experience.archivedAtTs != null;
        const isActive = experience.status === "started";
        const isInactive =
          experience.status === "pending" || experience.status === "paused";

        return statusFilters.some((filter) => {
          if (filter === "Archived") return isArchived;
          if (filter === "Active") return isActive && !isArchived;
          if (filter === "Inactive") return isInactive && !isArchived;
          return false;
        });
      });
    }

    if (typeFilters.length > 0) {
      filtered = filtered.filter((experience) => {
        if (renderLocation === "offers") {
          if (
            !experience.variations ||
            experience.variations.length === 0 ||
            !experience.variations[0].offer
          ) {
            return false;
          }

          const offer = experience.variations[0].offer;
          const { discountApplicationType, discountApplicationMethod } = offer;

          return typeFilters.some((filter) => {
            if (filter === "Volume Discount") {
              return discountApplicationType === "tieredDiscount";
            }
            if (filter === "Amount off Products") {
              return (
                discountApplicationType === "cartDiscount" &&
                discountApplicationMethod === "perItem"
              );
            }
            if (filter === "Amount off Order") {
              return (
                discountApplicationType === "cartDiscount" &&
                discountApplicationMethod === "perOrder"
              );
            }
            if (filter === "Free Gift") {
              return discountApplicationType === "freeGift";
            }
            if (filter === "Buy X Get Y") {
              return discountApplicationType === "buyXGetY";
            }
            if (filter === "Free Shipping") {
              return discountApplicationType === "freeShipping";
            }
            return false;
          });
        } else if (renderLocation === "siteContent") {
          return typeFilters.some((filter) => {
            if (filter === "Redirects") {
              return experience.testTypes.hasTestContentUrl;
            }
            if (filter === "Price") {
              return experience.testTypes.hasTestPricing;
            }

            if (filter === "Content Edits") {
              return (
                experience.testTypes.hasTestContentOnsite ||
                experience.testTypes.hasTestOnsiteInjections
              );
            }
            if (filter === "Template") {
              return experience.testTypes.hasTestContentTemplate;
            }
            if (filter === "Theme") {
              return experience.testTypes.hasTestContentTheme;
            }
            return false;
          });
        } else {
          return typeFilters.some((filter) => {
            if (filter === "Redirects") {
              return experience.testTypes.hasTestContentUrl;
            }
            if (filter === "Price") {
              return experience.testTypes.hasTestPricing;
            }

            if (filter === "Content Edits") {
              return (
                experience.testTypes.hasTestContentOnsite ||
                experience.testTypes.hasTestOnsiteInjections
              );
            }
            if (filter === "Template") {
              return experience.testTypes.hasTestContentTemplate;
            }
            if (filter === "Theme") {
              return experience.testTypes.hasTestContentTheme;
            }
            if (filter === "Offers") {
              return experience.testTypes.hasTestCampaign;
            }
            return false;
          });
        }
      });
    }

    return filtered;
  }, [experiences, searchInput, typeFilters, statusFilters]);
};
